import axios from "axios";
import { db } from "@/firebase-init";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  getDocs,
  where,
  getDoc,
  doc,
  limit,
} from "firebase/firestore";
import { auth } from "@/firebase-init.js";

// Get the Firebase user ID token
async function getFirebaseUserIdToken() {
  let userIdToken = auth.currentUser
    ? await auth.currentUser.getIdToken()
    : null;
  return userIdToken;
}

export default {
  async getChatResponse(
    { commit, state, dispatch },
    { message, senderUserId }
  ) {
    // Get the Firebase user ID token
    const userIdToken = await getFirebaseUserIdToken();

    // Check if the last message in the chat is from the user
    const lastMessage = state.chat[state.chat.length - 1];
    if (lastMessage && lastMessage.senderID !== "AI") {
      throw new Error(
        "Please wait for the assistant to respond before sending another message"
      );
    }

    // Set loading state to true
    commit("SET_LOADING", true);

    // Send the message to the AI agent
    try {
      const response = await axios.post(
        process.env.VUE_APP_AI_MATCHMAKER_ENDPOINT,
        {
          message,
          threadId: state.threadId,
          senderUserId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userIdToken}`,
          },
        }
      );

      const data = response.data;

      // Set the thread ID if it is not already set
      if (data.Thread_ID && !state.threadId) {
        commit("SET_THREAD_ID", data.Thread_ID);

        // If we're getting a new thread ID, ensure we start a listener for it
        await dispatch("startChatListener", { userId: senderUserId });

        // Also refresh chat history to show the new thread
        dispatch("fetchChatHistory");
      }

      return data.AI_Agent_Response;
    } catch (error) {
      // console.error("Error in getChatResponse:", error);
      throw new Error("Unable to get response from AI agent");
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async startChatListener({ commit, state }, { userId, forceNew = false }) {
    // Always clear the previous thread if forceNew is true
    if (forceNew) {
      commit("SET_THREAD_ID", null);
      commit("CLEAR_CHAT");
    }

    // If threadId is not set or forceNew is true, we need to find or create a thread
    if (!state.threadId || forceNew) {
      try {
        // If forceNew, we don't look for existing threads
        if (!forceNew) {
          const threadsRef = collection(db, "ai_matchmaker_chats");
          const threadsQuery = query(threadsRef, where("userId", "==", userId));
          const snapshot = await getDocs(threadsQuery);

          if (!snapshot.empty) {
            // Sort threads by lastMessageTime to get the most recent one
            const threads = snapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));

            // Sort by lastMessageTime (descending)
            threads.sort((a, b) => {
              const timeA = a.lastMessageTime
                ? new Date(a.lastMessageTime).getTime()
                : 0;
              const timeB = b.lastMessageTime
                ? new Date(b.lastMessageTime).getTime()
                : 0;
              return timeB - timeA;
            });

            // Use the most recent thread
            if (threads.length > 0) {
              commit("SET_THREAD_ID", threads[0].id);
              return; // We found an existing thread, so we're done with the thread search
            }
          }
        }

        // If forceNew is true or we didn't find a thread, we'll continue
        // without setting threadId, so a new one will be created by the backend
      } catch (error) {
        console.error("Error fetching threads:", error);
        return;
      }
    }

    if (state.threadId) {
      const q = query(
        collection(db, "ai_matchmaker_chats", state.threadId, "messages"),
        orderBy("timestamp", "asc")
      );

      try {
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const messages = snapshot.docs.map((doc) => ({
            id: doc.id,
            continuation: doc.data().continuation,
            message: doc.data().message,
            senderID: doc.data().senderID,
            timestamp: doc.data().timestamp,
          }));
          commit("SET_CHAT", messages);
        });

        commit("SET_UNSUBSCRIBE", unsubscribe);
      } catch (error) {
        console.error("Firestore listener error:", error);
      }
    }
  },

  stopChatListener({ commit }) {
    commit("CLEAR_UNSUBSCRIBE");
  },

  clearThread({ commit }) {
    commit("SET_THREAD_ID", null);
    commit("CLEAR_CHAT"); // Reset chat to greeting message
    commit("CLEAR_UNSUBSCRIBE");
  },

  // New action to fetch user's chat history
  async fetchChatHistory({ commit, rootGetters }) {
    const userId = rootGetters["auth/userId"];
    if (!userId) return;

    commit("SET_HISTORY_LOADING", true);

    try {
      // Query chats collection for this user without requiring composite index
      const threadsRef = collection(db, "ai_matchmaker_chats");
      const threadsQuery = query(threadsRef, where("userId", "==", userId));

      const snapshot = await getDocs(threadsQuery);
      // Sort the results client-side instead of using orderBy which requires an index
      const threads = [];

      // For each thread, get a preview of messages
      for (const threadDoc of snapshot.docs) {
        const threadData = threadDoc.data();

        // Get message previews (last few messages)
        const messagesQuery = query(
          collection(db, "ai_matchmaker_chats", threadDoc.id, "messages"),
          orderBy("timestamp", "desc"),
          limit(5) // Get last 5 messages for preview
        );

        const messagesSnapshot = await getDocs(messagesQuery);
        const messages = messagesSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .reverse(); // Reverse to get chronological order

        // Sort messages by timestamp to find the most recent one
        const sortedMessages = [...messages].sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeB - timeA; // Descending order (newest first)
        });

        // Get the most recent message timestamp
        const lastMessageTime =
          sortedMessages.length > 0 && sortedMessages[0].timestamp
            ? sortedMessages[0].timestamp
            : threadData.createdAt || new Date(0).toISOString();

        threads.push({
          id: threadDoc.id,
          userId: threadData.userId,
          createdAt: threadData.createdAt,
          lastMessageTime: lastMessageTime,
          messages: messages, // Keep original order for display
        });
      }

      // Sort threads by lastMessageTime descending (newest first)
      threads.sort((a, b) => {
        const timeA = a.lastMessageTime
          ? new Date(a.lastMessageTime).getTime()
          : 0;
        const timeB = b.lastMessageTime
          ? new Date(b.lastMessageTime).getTime()
          : 0;
        return timeB - timeA; // Descending order
      });

      commit("SET_CHAT_HISTORY", threads);
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      commit("SET_HISTORY_LOADING", false);
    }
  },

  // Load a specific chat thread
  async loadChatThread({ commit, dispatch }, { threadId }) {
    if (!threadId) return;

    commit("SET_LOADING", true);

    try {
      // Stop any existing listener
      dispatch("stopChatListener");

      // Get thread data
      const threadDoc = await getDoc(doc(db, "ai_matchmaker_chats", threadId));

      if (!threadDoc.exists()) {
        throw new Error("Chat thread not found");
      }

      // Set the thread ID
      commit("SET_THREAD_ID", threadId);

      // Start a new listener for this thread
      await dispatch("startChatListener", { userId: threadDoc.data().userId });

      return true;
    } catch (error) {
      console.error("Error loading chat thread:", error);
      return false;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};
